import { Component, OnInit, OnDestroy, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Task, TasksService } from '../api/tasks.service';
import { InboxService } from '../api/inbox.service';
import { FatipOidcAuthService } from '../auth/fatip-oidc.service';
import { FapIconComponent } from '../ui/fap-icon.component';

/**
 * Header bell: shows a badge with the count of actionable tasks for the
 * current persona and opens a small dropdown with the top items. Click on an
 * item navigates to its source (application detail, campaign detail, …);
 * "Alle anzeigen" goes to the bare /modules/worklist, which opens the tasks
 * tab through the component default. The two counter links and the approvals
 * footer link carry the tab as a query parameter. This line named
 * /modules/tasks until cut K4 (0.72.36) turned that path into a redirect to
 * the worklist.
 *
 * Polling every 30s is enough for a pilot — later we can upgrade to an
 * SSE/websocket push if the UX warrants it.
 */
@Component({
  selector: 'fap-task-inbox',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, FapIconComponent],
  template: `
    <div class="inbox" (click)="$event.stopPropagation()">
      <button type="button" class="bell" [attr.aria-expanded]="open()" [attr.aria-label]="ariaLabel()"
              [title]="ariaLabel()" (click)="toggle()">
        <span class="bell-icon" aria-hidden="true">
          <fap-icon name="inbox" [size]="20"></fap-icon>
        </span>
        @if (totalCount() > 0) {
          <span class="badge" [class.urgent]="hasOverdue()">{{ totalCount() }}</span>
        }
      </button>
      @if (open()) {
        <div class="menu" role="menu">
          <header>
            <strong>{{ 'i18n.fap.inbox.title' | translate }}</strong>
            @if (totalCount() > 0) {
              <span class="muted">· {{ totalCount() }}</span>
            }
          </header>
          <!-- 0.57.70 W-22: zwei Sub-Counts (Aufgaben + Approvals) damit
               der User sofort sieht wie sich die Arbeit verteilt. Klick
               auf einen der Sub-Counts springt direkt in den passenden
               Worklist-Tab. -->
          <nav class="kind-row">
            <a routerLink="/modules/worklist" [queryParams]="{tab:'tasks'}" (click)="open.set(false)">
              <fap-icon name="clipboard" [size]="14"></fap-icon> {{ 'i18n.fap.inbox.kind_tasks' | translate }}
              <span class="kind-count">{{ count() }}</span>
            </a>
            <a routerLink="/modules/worklist" [queryParams]="{tab:'approvals'}" (click)="open.set(false)">
              <fap-icon name="checkmark" [size]="14"></fap-icon> {{ 'i18n.fap.inbox.kind_approvals' | translate }}
              <span class="kind-count">{{ approvalCount() }}</span>
            </a>
          </nav>
          @if (totalCount() === 0) {
            <p class="empty">{{ 'i18n.fap.inbox.empty' | translate }}</p>
          } @else if (count() > 0) {
            <ul>
              @for (t of topTasks(); track t.id) {
                <li>
                  <a [routerLink]="t.action_url" (click)="open.set(false)">
                    <span class="dot dot--{{ t.urgency }}" aria-hidden="true"></span>
                    <span class="body">
                      <span class="title">{{ t.title }}</span>
                      <span class="subtitle">{{ t.subtitle }}</span>
                    </span>
                  </a>
                </li>
              }
            </ul>
            <footer>
              <a routerLink="/modules/worklist" (click)="open.set(false)">
                {{ 'i18n.fap.inbox.all' | translate }} →
              </a>
            </footer>
          } @else {
            <!-- nur Approvals offen, keine Tasks → Footer zeigt direkt
                 zum Approvals-Tab -->
            <footer>
              <a routerLink="/modules/worklist" [queryParams]="{tab:'approvals'}" (click)="open.set(false)">
                {{ 'i18n.fap.inbox.all_approvals' | translate }} →
              </a>
            </footer>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host { position: relative; display: inline-flex; }
    .inbox { position: relative; }
    .bell { position: relative; background: transparent; border: 1px solid transparent; padding: 6px 10px; cursor: pointer; border-radius: 3px; font-size: 1.3rem; line-height: 1; }
    .bell:hover { border-color: var(--ob-border-color, #acb4bd); }
    .bell-icon { color: var(--ob-text-medium); }
    .badge { position: absolute; top: 0; right: 0; background: var(--ob-primary); color: white; font-size: 0.7rem; font-weight: 700; padding: 1px 6px; border-radius: 10px; min-width: 18px; text-align: center; }
    .badge.urgent { background: var(--ob-error); }
    .menu { position: absolute; top: calc(100% + 4px); right: 0; width: 360px; max-height: 480px; overflow-y: auto; background: white; border: 1px solid #d4d4d4; border-radius: 3px; box-shadow: 0 4px 16px rgba(0,0,0,0.12); z-index: 50; }
    .menu header { padding: 10px 14px; border-bottom: 1px solid var(--ob-border-color); font-size: 0.92rem; }
    .menu .muted { color: var(--ob-text-muted); font-weight: normal; }
    .menu .empty { padding: 16px 14px; color: var(--ob-text-muted); font-size: 0.9rem; margin: 0; }
    .menu ul { list-style: none; padding: 0; margin: 0; }
    .menu li { border-bottom: 1px solid #f1f1f1; }
    .menu li:last-child { border-bottom: none; }
    .menu li a { display: flex; gap: 10px; padding: 10px 14px; color: inherit; text-decoration: none; align-items: flex-start; }
    .menu li a:hover { background: var(--ob-surface-panel); }
    .dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-top: 6px; flex: 0 0 auto; }
    .dot--normal { background: #b5b5b5; }
    .dot--soon { background: var(--ob-warning); }
    .dot--overdue { background: var(--ob-error); }
    .body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .title { font-weight: 600; font-size: 0.9rem; color: var(--ob-text-ink); }
    .subtitle { font-size: 0.8rem; color: var(--ob-text-muted); }
    .menu footer { padding: 10px 14px; border-top: 1px solid var(--ob-border-color); text-align: right; }
    .menu footer a { color: var(--ob-primary); text-decoration: none; font-size: 0.9rem; }
    /* 0.57.70 — Kind-Row mit zwei sub-counts. */
    .kind-row { display: flex; gap: 6px; padding: 8px 14px; border-bottom: 1px solid #f1f1f1; }
    .kind-row a {
      flex: 1; display: inline-flex; align-items: center; justify-content: space-between;
      padding: 6px 10px; border-radius: 3px; font-size: 0.85rem;
      color: var(--ob-text); text-decoration: none;
      background: var(--ob-surface-alt, #f0f4f7);
    }
    .kind-row a:hover { background: var(--ob-primary-soft, #ffedee); color: var(--ob-primary); }
    .kind-count { font-weight: 700; font-size: 0.78rem; padding: 1px 6px; border-radius: 8px; background: white; }
  `]
})
export class TaskInboxComponent implements OnInit, OnDestroy {
  private svc = inject(TasksService);
  private inboxSvc = inject(InboxService);
  // 0.62.0.2: FakeAuthService -> FatipOidcAuthService
  private oidc = inject(FatipOidcAuthService);

  tasks = signal<Task[]>([]);
  approvalCount = signal<number>(0);
  open = signal(false);
  count = () => this.tasks().length;
  // 0.57.70 W-22: Brief-Icon zeigt Summe aus Aufgaben + Approvals,
  // damit der User auf einen Blick weiß ob er handeln sollte.
  totalCount = () => this.count() + this.approvalCount();
  hasOverdue = () => this.tasks().some(t => t.urgency === 'overdue');
  topTasks = () => this.tasks().slice(0, 5);
  ariaLabel = () => `Worklist: ${this.totalCount()} (Aufgaben ${this.count()}, Genehmigungen ${this.approvalCount()})`;

  private timer: ReturnType<typeof setInterval> | null = null;
  private clickAway = (_ev: MouseEvent) => { if (this.open()) this.open.set(false); };

  constructor() {
    // 0.62.0.2: Re-fetch whenever OIDC subject changes (login/logout)
    // so the badge reflects the current user's visible tasks immediately.
    effect(() => {
      this.oidc.subject();
      this.refresh();
    });
  }

  ngOnInit() {
    // U26: der Abruf läuft im Konstruktor-Effect (erster Run deckt den Mount
    // ab, jeder Subjektwechsel triggert neu); hier nur Timer und Listener.
    this.timer = setInterval(() => this.refresh(), 30_000);
    document.addEventListener('click', this.clickAway);
  }

  ngOnDestroy() {
    if (this.timer) clearInterval(this.timer);
    document.removeEventListener('click', this.clickAway);
  }

  toggle() { this.open.update(v => !v); }

  refresh() {
    this.svc.list().subscribe({
      next: r => this.tasks.set(
        (r.items ?? []).sort((a, b) => this.urgencyWeight(b.urgency) - this.urgencyWeight(a.urgency))
      ),
      error: () => this.tasks.set([])
    });
    // 0.57.70 W-22: Approvals-Count fürs Badge.
    this.inboxSvc.list({ status: 'pending' }).subscribe({
      next: r => this.approvalCount.set((r.items ?? []).length),
      error: () => this.approvalCount.set(0),
    });
  }

  private urgencyWeight(u: Task['urgency']): number {
    return u === 'overdue' ? 2 : u === 'soon' ? 1 : 0;
  }
}
